/**
 * Types for Bender's Sync Target registry and Box folder probe.
 *
 * These are hand-written rather than generated: `bender-api.ts` is a hand-written
 * RTK Query slice (Leela's codegen only covers its own backend's OpenAPI spec).
 * They mirror Bender's `SyncTargetDto` / `CreateSyncTargetDto` / `UpdateSyncTargetDto`
 * with `Date` fields as ISO `string`s, which is what crosses the wire.
 *
 * This frontend must deploy before Bender's nested-only response ships. During rollout, this
 * reader accepts both Bender's current flat shape and its future nested shape; consumers normalize
 * at the read boundary, and the API slice deliberately passes both wire shapes through unchanged.
 * Remove the legacy fields after Bender CR-303996263 deploys.
 *
 * This mirror includes Bender's exclusion fields so Leela can display and edit them,
 * while schedule and status editing remain intentionally outside this UI's scope.
 *
 * See docs/system-specs/modules/box-folders.md
 */

/** Bender's only sync target type today. Note the value is lowercase. */
export const SyncTargetTypes = {
  BOX: 'box',
} as const;
export type SyncTargetType = (typeof SyncTargetTypes)[keyof typeof SyncTargetTypes];

/**
 * `ACTIVE` / `PAUSED` are the only statuses an API caller can set. `ERROR`,
 * `QUEUED`, and `IN_PROGRESS` are pipeline-managed and rejected on PATCH.
 */
export const SyncTargetStatuses = {
  ACTIVE: 'ACTIVE',
  PAUSED: 'PAUSED',
  ERROR: 'ERROR',
  QUEUED: 'QUEUED',
  IN_PROGRESS: 'IN_PROGRESS',
} as const;
export type SyncTargetStatus = (typeof SyncTargetStatuses)[keyof typeof SyncTargetStatuses];

export const SyncTargetErrorTypes = {
  ACCESS_LOST: 'ACCESS_LOST',
  SCHEDULE_PROVISIONING_FAILED: 'SCHEDULE_PROVISIONING_FAILED',
  ENUMERATION_FAILED: 'ENUMERATION_FAILED',
} as const;
export type SyncTargetErrorType = (typeof SyncTargetErrorTypes)[keyof typeof SyncTargetErrorTypes];

/**
 * One enumeration snapshot. The object itself is absent until a target has enumerated.
 * Once present, missing fields remain meaningful and must never be defaulted or filled from
 * the legacy flat contract.
 */
export interface EnumerationDto {
  /** Completion of the most recent finished scan, which may belong to the previous run. */
  completedAt?: string;
  /** Unique files discovered before qualification; this is not an assets-added count. */
  fileCount?: number;
  /** Enqueued batches. Absence means no denominator exists yet; zero means no work. */
  unitCount?: number;
  /** Finished batches. Absence, zero, and an over-shoot each carry distinct meaning. */
  qualifiedUnitCount?: number;
  /** When the worker last finished a batch for the current run. */
  lastQualifiedAt?: string;
}

export interface SyncTargetDto {
  /** Composite registry key, `"{syncTargetType}:{targetId}"` — e.g. `box:409789436486`. */
  syncTargetId: string;
  syncTargetType: SyncTargetType;
  /** Bare numeric Box folder id, canonicalized server-side. */
  targetId: string;
  /** EventBridge Scheduler expression — `rate(...)` or `cron(...)`. */
  refreshSchedule: string;
  status: SyncTargetStatus;
  /** Box folder IDs whose subtrees are excluded from qualification. Absent when never set. */
  excludedFolderIds?: string[];
  /** Case-insensitive regex sources matched against filenames and folder paths. Absent when never set. */
  excludedPathnames?: string[];
  /**
   * Retained across refresh and pause. Only meaningful when `status` is `ERROR`
   * — never render these based on presence alone or you show resolved errors forever.
   */
  errorType?: SyncTargetErrorType;
  errorAt?: string;
  /**
   * The nested enumeration contract. Presence is authoritative even when the object is empty,
   * because mixing in legacy fields would erase the absence semantics used by progress states.
   */
  enumeration?: EnumerationDto;
  /**
   * @deprecated Legacy flat enumeration shape retained only through Bender's nested-contract
   * rollout. Remove after every deployed Bender serves `enumeration`.
   */
  lastEnumerationAt?: string;
  /**
   * @deprecated Legacy flat enumeration shape retained only through Bender's nested-contract
   * rollout. Remove after every deployed Bender serves `enumeration`.
   */
  lastEnumerationFileCount?: number;
  /**
   * @deprecated Legacy flat enumeration shape retained only through Bender's nested-contract
   * rollout. Remove after every deployed Bender serves `enumeration`.
   */
  lastEnumerationUnitCount?: number;
  /**
   * @deprecated Legacy flat enumeration shape retained only through Bender's nested-contract
   * rollout. Remove after every deployed Bender serves `enumeration`.
   */
  qualifiedUnitCount?: number;
  /**
   * @deprecated Legacy flat enumeration shape retained only through Bender's nested-contract
   * rollout. Remove after every deployed Bender serves `enumeration`.
   */
  lastQualifiedUnitAt?: string;
  createdAt?: string;
  updatedAt?: string;
  userAlias: string;
}

export interface SyncTargetListResponse {
  data: SyncTargetDto[];
  meta: {
    pagination: {
      page: number;
      pageSize: number;
      pageCount: number;
      total: number;
    };
  };
}

/** `page` is 1-indexed (default 1); `pageSize` is 1..100 (default 20). */
export interface SyncTargetListArgs {
  page?: number;
  pageSize?: number;
}

export interface CreateSyncTargetRequest {
  /**
   * Always send this explicitly. Bender defaults it via a class field initializer,
   * which is not something to depend on across a wire boundary.
   */
  syncTargetType: SyncTargetType;
  /** Bender accepts a bare numeric id or a Box folder URL; we always send the bare id. */
  targetId: string;
  refreshSchedule: string;
  userAlias: string;
}

/** Args for PATCH /sync-targets/{id}. Both arrays always sent in full (Bender replaces wholesale). */
export interface UpdateSyncTargetRequest {
  syncTargetId: string;
  excludedFolderIds: string[];
  excludedPathnames: string[];
}

/** Response of Bender's `GET /box?folderId=` folder-lookup mode. */
export interface BoxFolder {
  id: string;
  name: string;
  etag?: string;
  itemStatus?: string;
  itemCollection?: {
    totalCount: number;
  };
}
